/**
 * Custom Dropdown Web Component
 *
 * A variant selector dropdown that displays as a standard dropdown on desktop
 * and as a bottom sheet with dimming overlay on mobile.
 *
 * MOBILE BEHAVIOR (< 750px):
 * - Shows as bottom sheet sliding up from screen bottom
 * - Dimming overlay appears behind the sheet
 * - Body scroll is locked while open
 * - Tapping overlay closes the dropdown
 *
 * DESKTOP BEHAVIOR (>= 750px):
 * - Shows as standard dropdown below the button
 * - No overlay (handled via CSS display: none)
 * - No scroll lock needed
 *
 * ROTATION HANDLING:
 * We store viewport state (mobile vs desktop) when the dropdown OPENS.
 * This ensures consistent behavior if the user rotates their device while
 * the dropdown is open:
 * - If opened on mobile → uses mobile close behavior (animation, scroll unlock)
 * - If opened on desktop → uses desktop close behavior (instant, no scroll)
 * This prevents bugs like scroll lock never being released if user opens
 * on mobile and rotates to desktop before closing.
 */
if (!customElements.get("custom-dropdown")) {
  customElements.define(
    "custom-dropdown",
    class CustomDropdown extends HTMLElement {
      /**
       * Mobile breakpoint media query.
       * IMPORTANT: Must match the CSS media queries in component-custom-dropdown.css
       * CSS uses: @media screen and (max-width: 749px) for mobile
       *           @media screen and (min-width: 750px) for desktop
       */
      static MOBILE_BREAKPOINT = "(max-width: 749px)";

      /**
       * Static counter for body scroll lock.
       * Multiple dropdowns may be on the same page. We use a counter instead
       * of a boolean so that opening dropdown A, then dropdown B, then closing
       * dropdown A doesn't accidentally restore scroll while B is still open.
       */
      static scrollLockCount = 0;

      /**
       * Increment the scroll lock counter and lock body scroll if this is
       * the first dropdown to request it.
       */
      static incrementScrollLock() {
        CustomDropdown.scrollLockCount++;
        if (CustomDropdown.scrollLockCount === 1) {
          document.body.style.overflow = "hidden";
        }
      }

      /**
       * Decrement the scroll lock counter and restore body scroll if no
       * dropdowns need it anymore. Uses Math.max to prevent negative counts
       * from potential edge cases.
       */
      static decrementScrollLock() {
        CustomDropdown.scrollLockCount = Math.max(
          0,
          CustomDropdown.scrollLockCount - 1,
        );
        if (CustomDropdown.scrollLockCount === 0) {
          document.body.style.overflow = "";
        }
      }

      constructor() {
        super();

        // Core state
        this.isOpen = false;
        this.currentIndex = -1; // Keyboard navigation index

        // Variant data for inventory display
        this.variantData = null;
        this.variantSubscription = null;

        /**
         * ROTATION EDGE CASE HANDLING:
         * Stores whether the dropdown was opened on mobile viewport.
         *
         * Why this matters:
         * 1. User opens dropdown on mobile (< 750px) → scroll locked, overlay shown
         * 2. User rotates device to landscape (now >= 750px, desktop mode)
         * 3. User closes dropdown
         *
         * Without this flag, closeDropdown() would check current viewport (desktop)
         * and skip scroll unlock, leaving body with overflow:hidden forever.
         *
         * With this flag, we remember "opened on mobile" and properly clean up
         * mobile-specific state (scroll lock, animations) regardless of
         * current viewport size.
         */
        this.openedOnMobile = false;

        /**
         * MediaQueryList for detecting viewport changes.
         * Used to update overlay visibility when user rotates device
         * or resizes window while dropdown is open.
         */
        this.mobileMediaQuery = window.matchMedia(
          CustomDropdown.MOBILE_BREAKPOINT,
        );

        // Bind methods that need stable references for event listener cleanup
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
      }

      /**
       * Called when element is added to DOM.
       * Sets up all DOM references and event listeners.
       */
      connectedCallback() {
        // Get references to DOM elements
        this.hiddenSelect = document.getElementById(this.dataset.hiddenSelect);
        this.button = this.querySelector("[data-dropdown-button]");
        this.menu = this.querySelector("[data-dropdown-menu]");
        this.selectedText = this.querySelector("[data-selected-text]");
        this.overlay = this.querySelector("[data-dropdown-overlay]");

        if (!this.button || !this.menu || !this.hiddenSelect) {
          console.error("Custom dropdown: Required elements not found");
          return;
        }

        // Load variant data for inventory calculations
        this.loadVariantData();

        // Setup event listeners
        this.setupEventListeners();

        // Setup keyboard navigation
        this.setupKeyboardNav();

        // Listen for other option changes to update inventory
        this.subscribeToVariantChanges();

        // Select first in-stock variant if available
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          this.selectFirstInStockVariant();
        });

        /**
         * Listen for viewport changes (rotation, resize).
         * Updates overlay visibility when crossing mobile/desktop breakpoint
         * while dropdown is open.
         */
        this.mobileMediaQuery.addEventListener(
          "change",
          this.handleViewportChange,
        );
      }

      /**
       * Called when element is removed from DOM.
       * Critical for cleanup to prevent memory leaks and stuck states.
       */
      disconnectedCallback() {
        // Remove document-level click listener to prevent memory leaks
        document.removeEventListener("click", this.handleOutsideClick);

        // Remove viewport change listener
        this.mobileMediaQuery.removeEventListener(
          "change",
          this.handleViewportChange,
        );

        // Unsubscribe from PubSub events
        if (this.variantSubscription) {
          this.variantSubscription();
          this.variantSubscription = null;
        }

        /**
         * DEFENSIVE CLEANUP:
         * If the component is removed while dropdown is open (e.g., page
         * navigation, AJAX content replacement), we must clean up mobile state.
         *
         * We use openedOnMobile (not current viewport) because the user might
         * have rotated the device while dropdown was open.
         */
        if (this.isOpen && this.openedOnMobile) {
          CustomDropdown.decrementScrollLock();
        }

        // Restore reparented elements if component is removed while open
        if (this._menuOriginalParent && this.menu) {
          this.menu.classList.remove("custom-dropdown--sheet");
          this._menuOriginalParent.insertBefore(
            this.menu,
            this._menuOriginalNext,
          );
          this._menuOriginalParent = null;
          this._menuOriginalNext = null;
        }
        if (this._overlayOriginalParent && this.overlay) {
          this.overlay.classList.remove("is-reparented");
          this._overlayOriginalParent.insertBefore(
            this.overlay,
            this._overlayOriginalNext,
          );
          this._overlayOriginalParent = null;
          this._overlayOriginalNext = null;
        }
        if (this._desktopPositioned && this.menu) {
          this.menu.style.cssText = "";
          this._desktopPositioned = false;
        }

        // Reset all state
        this.isOpen = false;
        this.openedOnMobile = false;
      }

      /**
       * Handles clicks outside the dropdown to close it.
       * Bound in constructor for stable reference.
       */
      handleOutsideClick(e) {
        if (
          !this.contains(e.target) &&
          !(this.menu && this.menu.contains(e.target)) &&
          !(this.overlay && this.overlay.contains(e.target)) &&
          this.isOpen
        ) {
          this.closeDropdown();
        }
      }

      /**
       * Handles viewport changes (device rotation, window resize).
       * Updates overlay visibility and scroll lock when crossing the
       * mobile/desktop breakpoint while the dropdown is open.
       *
       * Scenarios:
       * 1. Mobile → Desktop while open: Hide overlay, remove scroll lock
       * 2. Desktop → Mobile while open: Show overlay, add scroll lock
       *
       * @param {MediaQueryListEvent} e - The media query change event
       */
      handleViewportChange(e) {
        if (!this.isOpen) return;

        const nowMobile = e.matches;
        const wasMobile = this.openedOnMobile;

        if (nowMobile && !wasMobile) {
          // Desktop → Mobile: Show overlay, add scroll lock
          if (this.overlay) {
            this.overlay.classList.add("is-visible");
          }
          CustomDropdown.incrementScrollLock();
          this.openedOnMobile = true;
        } else if (!nowMobile && wasMobile) {
          // Mobile → Desktop: Hide overlay, remove scroll lock
          if (this.overlay) {
            this.overlay.classList.remove("is-visible");
          }
          CustomDropdown.decrementScrollLock();
          this.openedOnMobile = false;
        }
      }

      /**
       * Loads variant data from the page for inventory calculations.
       * Data is embedded in [data-product-variants] script tag as JSON.
       */
      loadVariantData() {
        const productDataEl = document.querySelector("[data-product-variants]");
        if (productDataEl) {
          try {
            this.variantData = JSON.parse(productDataEl.textContent);
            this.updateInventoryDisplay();
          } catch (e) {
            console.error("Failed to parse variant data:", e);
          }
        }
      }

      /**
       * Updates inventory display for each option based on currently
       * selected values of other variant options.
       */
      updateInventoryDisplay() {
        if (!this.variantData) return;

        const showInventory = this.dataset.showInventory === "true";
        const threshold = parseInt(this.dataset.inventoryThreshold);
        const optionPosition = parseInt(this.dataset.optionPosition);

        // Get currently selected values from other options
        const variantSelects = this.closest("variant-selects");
        if (!variantSelects) return;

        const allSelects = variantSelects.querySelectorAll(
          'select, input[type="radio"]:checked',
        );
        const selectedValues = Array.from(allSelects).map((el) => {
          if (el.tagName === "SELECT") {
            return el.value;
          } else if (el.tagName === "INPUT") {
            return el.value;
          }
        });

        // Update each option's inventory display
        this.menu.querySelectorAll('[role="option"]').forEach((optionEl) => {
          const optionValue = optionEl.dataset.optionValue;

          // Build option values array with this value
          const testValues = [...selectedValues];
          testValues[optionPosition - 1] = optionValue;

          // Find matching variants
          const matchingVariants = this.variantData.filter((variant) => {
            return variant.options.every((opt, i) => {
              return !testValues[i] || opt === testValues[i];
            });
          });

          // Calculate inventory
          let totalInventory = 0;
          let anyAvailable = false;

          matchingVariants.forEach((variant) => {
            if (variant.available) anyAvailable = true;
            if (variant.inventory_quantity !== null) {
              totalInventory += variant.inventory_quantity;
            }
          });

          // Update display
          const inventoryEl = optionEl.querySelector(
            "[data-inventory-display]",
          );
          if (!inventoryEl) return;

          if (
            showInventory &&
            anyAvailable &&
            totalInventory > 0 &&
            totalInventory <= threshold
          ) {
            inventoryEl.innerHTML = `<span class="custom-dropdown__low-stock">${totalInventory} LEFT</span>`;
          } else if (!anyAvailable) {
            inventoryEl.innerHTML = `<span class="custom-dropdown__sold-out">SOLD OUT</span>`;
          } else {
            inventoryEl.innerHTML = "";
          }
        });
      }

      /**
       * Selects the first in-stock option on initialization.
       * Priority order:
       * 1. First option with available=true AND inventory_quantity > 0
       * 2. First option with available=true (no stock requirement)
       * 3. First option (fallback if all unavailable)
       */
      selectFirstInStockVariant() {
        if (!this.variantData) return;

        const optionPosition = parseInt(this.dataset.optionPosition);

        // Get currently selected values from other dropdowns
        const variantSelects = this.closest("variant-selects");
        if (!variantSelects) return;

        const allSelects = variantSelects.querySelectorAll(
          'select, input[type="radio"]:checked',
        );
        const selectedValues = Array.from(allSelects).map((el) => {
          if (el.tagName === "SELECT") {
            return el.value;
          } else if (el.tagName === "INPUT") {
            return el.value;
          }
        });

        // Map each option value to its inventory status
        const optionEls = this.menu.querySelectorAll('[role="option"]');
        const optionValueMap = [];

        optionEls.forEach((optionEl) => {
          const optionValue = optionEl.dataset.optionValue;

          // Build test values array with this option value
          const testValues = [...selectedValues];
          testValues[optionPosition - 1] = optionValue;

          // Find matching variants
          const matchingVariants = this.variantData.filter((variant) => {
            return variant.options.every((opt, i) => {
              return !testValues[i] || opt === testValues[i];
            });
          });

          // Calculate inventory
          let totalInventory = 0;
          let anyAvailable = false;

          matchingVariants.forEach((variant) => {
            if (variant.available) anyAvailable = true;
            if (variant.inventory_quantity !== null) {
              totalInventory += variant.inventory_quantity;
            }
          });

          optionValueMap.push({
            element: optionEl,
            value: optionValue,
            available: anyAvailable,
            inventory: totalInventory,
          });
        });

        // Find best option using priority order
        let targetOption = null;

        // Priority 1: Available with stock
        targetOption = optionValueMap.find(
          (opt) => opt.available && opt.inventory > 0,
        );

        // Priority 2: Available (no stock requirement)
        if (!targetOption) {
          targetOption = optionValueMap.find((opt) => opt.available);
        }

        // Priority 3: First option (fallback)
        if (!targetOption && optionValueMap.length > 0) {
          targetOption = optionValueMap[0];
        }

        // Select if different from current selection
        if (targetOption) {
          const currentSelected = this.menu.querySelector(
            '[aria-selected="true"]',
          );
          if (currentSelected !== targetOption.element) {
            // During initialization, update UI silently without triggering events
            // This prevents race conditions with server fetches
            this.updateUIToMatchVariant(targetOption.element);
          }
        }
      }

      /**
       * Sets up all event listeners for the dropdown.
       */
      setupEventListeners() {
        // Toggle dropdown on button click
        this.button.addEventListener("click", (e) => {
          e.preventDefault();
          this.toggleDropdown();
        });

        // Select option on click (works on both desktop and mobile)
        this.menu.addEventListener("click", (e) => {
          const optionEl = e.target.closest('[role="option"]');
          if (!optionEl) return;

          // Don't select unavailable options
          if (optionEl.getAttribute("aria-disabled") === "true") return;

          this.selectOption(optionEl);
        });

        // Close on outside click (use bound method for cleanup)
        document.addEventListener("click", this.handleOutsideClick);

        // Close on overlay click (mobile only, but listener is always attached)
        if (this.overlay) {
          this.overlay.addEventListener("click", () => {
            this.closeDropdown();
          });
        }
      }

      /**
       * Sets up keyboard navigation for accessibility.
       * Supports: Arrow keys, Enter, Space, Escape
       */
      setupKeyboardNav() {
        this.button.addEventListener("keydown", (e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!this.isOpen) this.openDropdown();
            this.navigateOptions(e.key === "ArrowDown" ? 1 : -1);
          } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (this.isOpen) {
              this.selectCurrentOption();
            } else {
              this.toggleDropdown();
            }
          } else if (e.key === "Escape" && this.isOpen) {
            this.closeDropdown();
          }
        });

        this.addEventListener("keydown", (e) => {
          if (!this.isOpen) return;

          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            this.navigateOptions(e.key === "ArrowDown" ? 1 : -1);
          } else if (e.key === "Enter") {
            e.preventDefault();
            this.selectCurrentOption();
          } else if (e.key === "Escape") {
            this.closeDropdown();
            this.button.focus();
          }
        });
      }

      /**
       * Navigates through options using keyboard.
       * @param {number} direction - 1 for next, -1 for previous
       */
      navigateOptions(direction) {
        const options = Array.from(
          this.menu.querySelectorAll(
            '[role="option"]:not([aria-disabled="true"])',
          ),
        );

        if (options.length === 0) return;

        this.currentIndex += direction;

        // Wrap around at boundaries
        if (this.currentIndex < 0) this.currentIndex = options.length - 1;
        if (this.currentIndex >= options.length) this.currentIndex = 0;

        // Update visual focus indicator
        this.menu.querySelectorAll('[role="option"]').forEach((opt) => {
          opt.classList.remove("custom-dropdown__option--focused");
        });
        options[this.currentIndex].classList.add(
          "custom-dropdown__option--focused",
        );

        // Ensure focused option is visible in scrollable menu
        options[this.currentIndex]?.scrollIntoView({ block: "nearest" });
      }

      /**
       * Selects the currently focused option (keyboard navigation).
       */
      selectCurrentOption() {
        const options = Array.from(
          this.menu.querySelectorAll(
            '[role="option"]:not([aria-disabled="true"])',
          ),
        );

        if (this.currentIndex >= 0 && this.currentIndex < options.length) {
          this.selectOption(options[this.currentIndex]);
        }
      }

      /**
       * Selects an option and updates the form.
       * @param {HTMLElement} optionEl - The option element to select
       */
      selectOption(optionEl) {
        const valueId = optionEl.dataset.optionValueId;

        // Update hidden select (used by VariantSelects for form submission)
        const selectOption = this.hiddenSelect.querySelector(
          `option[data-option-value-id="${valueId}"]`,
        );

        if (selectOption) {
          // Set new selection - ONLY set value, don't set attribute
          // The global.js updateSelectionMetadata() will handle the selected attribute
          this.hiddenSelect.value = selectOption.value;

          // Trigger change event for VariantSelects to handle variant switching
          const event = new Event("change", { bubbles: true });
          this.hiddenSelect.dispatchEvent(event);
        }

        // Update visible UI
        this.updateSelectedUI(optionEl);
        this.closeDropdown();
      }

      /**
       * Silently updates the dropdown UI to match a variant without triggering events.
       * Used during initialization to avoid race conditions with server fetches.
       * Does NOT fire change events - just updates visual state.
       *
       * @param {HTMLElement} optionEl - The option element to mark as selected
       */
      updateUIToMatchVariant(optionEl) {
        if (!optionEl) return;

        const valueId = optionEl.dataset.optionValueId;

        // Update hidden select value (but don't dispatch events!)
        const selectOption = this.hiddenSelect.querySelector(
          `option[data-option-value-id="${valueId}"]`,
        );

        if (selectOption) {
          // Set new selection - ONLY set value during initialization
          // No need to manipulate the selected attribute since no change event is dispatched
          this.hiddenSelect.value = selectOption.value;

          // NOTE: NOT dispatching change event here - this is intentional!
          // Prevents race conditions during initialization
        }

        // Update visible UI (dropdown button text and aria-selected)
        this.updateSelectedUI(optionEl);
      }

      /**
       * Updates the visible UI to reflect the selected option.
       * @param {HTMLElement} selectedOptionEl - The selected option element
       */
      updateSelectedUI(selectedOptionEl) {
        // Update button text to show selected value
        const label = selectedOptionEl.querySelector(
          ".custom-dropdown__option-label",
        );
        if (label) {
          this.selectedText.textContent = label.textContent.trim();
        }

        // Update aria-selected for accessibility
        this.menu.querySelectorAll('[role="option"]').forEach((opt) => {
          const isSelected = opt === selectedOptionEl;
          opt.setAttribute("aria-selected", isSelected);
        });
      }

      /**
       * Toggles dropdown open/closed state.
       */
      toggleDropdown() {
        if (this.isOpen) {
          this.closeDropdown();
        } else {
          this.openDropdown();
        }
      }

      /**
       * Opens the dropdown.
       *
       * DESKTOP: Shows dropdown below button, no overlay
       * MOBILE: Shows bottom sheet with slide animation and dimming overlay
       */
      openDropdown() {
        this.isOpen = true;

        /**
         * CRITICAL: Store viewport state NOW, at open time.
         *
         * This flag determines:
         * 1. Whether to show overlay (mobile only)
         * 2. Whether to lock body scroll (mobile only)
         * 3. Whether to animate (mobile only)
         * 4. How to clean up on close (even if viewport changed)
         *
         * See constructor comment for full rotation edge case explanation.
         */
        this.openedOnMobile = window.matchMedia(
          CustomDropdown.MOBILE_BREAKPOINT,
        ).matches;

        const inQuickAddModal = !!this.closest("quick-add-modal");

        // Update button ARIA state
        this.button.setAttribute("aria-expanded", "true");
        this.button.classList.add("custom-dropdown__button--open");

        // Remove hidden attribute to allow CSS transitions
        // (hidden attribute = display:none, which blocks transitions)
        this.menu.removeAttribute("hidden");
        this.menu.offsetHeight; // Force reflow before measuring

        // Inside quick-add modal: reparent menu outside the transform container.
        // quick-add-modal__content has transform:translate(-50%,-50%) which
        // breaks position:fixed, so we move the menu to quick-add-modal instead.
        if (inQuickAddModal) {
          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) {
            this._menuOriginalParent = this.menu.parentNode;
            this._menuOriginalNext = this.menu.nextSibling;
            quickAddModal.appendChild(this.menu);

            const rect = this.button.getBoundingClientRect();
            const menuHeight = this.menu.scrollHeight;
            const spaceBelow = window.innerHeight - rect.bottom;
            const GAP = 8;

            if (this.openedOnMobile || spaceBelow < menuHeight + GAP) {
              // Mobile or not enough space: bottom sheet
              this.openedOnMobile = true;
              this.menu.classList.add("custom-dropdown--sheet");
              if (this.overlay) {
                this._overlayOriginalParent = this.overlay.parentNode;
                this._overlayOriginalNext = this.overlay.nextSibling;
                this.overlay.classList.add("is-reparented");
                quickAddModal.appendChild(this.overlay);
              }
            } else {
              // Desktop with space: position below button
              this._desktopPositioned = true;
              this.menu.style.position = "fixed";
              this.menu.style.top = rect.bottom + GAP + "px";
              this.menu.style.left = rect.left + "px";
              this.menu.style.width = rect.width + "px";
              this.menu.style.bottom = "auto";
              this.menu.style.maxHeight = "30rem";
            }
          }
        }

        /**
         * OVERLAY HANDLING:
         * Only show overlay on mobile. On desktop, CSS sets display:none
         * on the overlay, but we also skip adding the class entirely
         * for cleaner state management.
         */
        if (this.overlay && this.openedOnMobile) {
          this.overlay.classList.add("is-visible");
        }

        /**
         * ANIMATION TRIGGER:
         * Force browser reflow before adding is-visible class.
         * This ensures the CSS transition plays (from translateY(100%) to 0).
         * Without this, the browser may batch the hidden removal and class
         * addition, skipping the animation entirely.
         */
        this.menu.offsetHeight; // Force reflow
        this.menu.classList.add("is-visible");

        // Reset keyboard navigation to currently selected option
        const selectedOption = this.menu.querySelector(
          '[aria-selected="true"]',
        );
        if (selectedOption) {
          const options = Array.from(
            this.menu.querySelectorAll(
              '[role="option"]:not([aria-disabled="true"])',
            ),
          );
          this.currentIndex = options.indexOf(selectedOption);
          selectedOption.classList.add("custom-dropdown__option--focused");
        }

        /**
         * SCROLL LOCK (mobile only):
         * Prevent background page scrolling while bottom sheet is open.
         * Uses static counter to handle multiple dropdowns correctly.
         */
        if (this.openedOnMobile) {
          CustomDropdown.incrementScrollLock();
        }
      }

      /**
       * Closes the dropdown.
       *
       * Uses stored openedOnMobile state to determine cleanup behavior,
       * ensuring correct cleanup even if device was rotated while open.
       */
      closeDropdown() {
        this.isOpen = false;

        // Update button ARIA state
        this.button.setAttribute("aria-expanded", "false");
        this.button.classList.remove("custom-dropdown__button--open");

        // Reset keyboard navigation
        this.currentIndex = -1;

        // Remove focus styling from all options
        this.menu.querySelectorAll('[role="option"]').forEach((opt) => {
          opt.classList.remove("custom-dropdown__option--focused");
        });

        /**
         * OVERLAY HANDLING:
         * Only manipulate overlay if we opened on mobile.
         * This handles the rotation edge case: if user opened on mobile
         * then rotated to desktop, we still need to clean up the overlay
         * class we added.
         */
        if (this.overlay && this.openedOnMobile) {
          this.overlay.classList.remove("is-visible");
        }

        // Remove visible class to trigger slide-out animation (mobile)
        // On desktop, there's no animation, so this is just cleanup
        this.menu.classList.remove("is-visible");

        /**
         * ANIMATION TIMING:
         * Get duration from CSS custom property for consistency.
         * Falls back to 200ms if variable is not defined.
         *
         * Both mobile and desktop now have animations:
         * - Mobile: slide-out animation (transform)
         * - Desktop: fade-out animation (opacity + transform)
         * Both use the same duration for consistency.
         */
        const animationDuration =
          parseInt(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--duration-default",
            ),
          ) || 200;

        /**
         * DEFERRED HIDDEN ATTRIBUTE:
         * We wait for the animation to complete before adding [hidden].
         * Adding [hidden] immediately would set display:none, canceling
         * the slide-out animation mid-way.
         *
         * The isOpen check prevents race conditions: if user rapidly
         * closes then opens, we don't want the delayed callback to
         * hide the newly opened menu.
         */
        setTimeout(() => {
          if (!this.isOpen) {
            this.menu.setAttribute("hidden", "");

            // Restore menu to original DOM position after animation completes
            // (doing it before would cause a visible jump as the menu loses
            // its sheet/fixed styles while still animating)
            if (this._desktopPositioned) {
              this.menu.style.cssText = "";
              this._desktopPositioned = false;
            }
            if (this._menuOriginalParent) {
              this.menu.classList.remove("custom-dropdown--sheet");
              this._menuOriginalParent.insertBefore(
                this.menu,
                this._menuOriginalNext,
              );
              this._menuOriginalParent = null;
              this._menuOriginalNext = null;
            }
            if (this._overlayOriginalParent) {
              this.overlay.classList.remove("is-reparented");
              this._overlayOriginalParent.insertBefore(
                this.overlay,
                this._overlayOriginalNext,
              );
              this._overlayOriginalParent = null;
              this._overlayOriginalNext = null;
            }
          }
        }, animationDuration);

        /**
         * SCROLL LOCK CLEANUP (mobile only):
         * Uses openedOnMobile (not current viewport) to handle rotation.
         * If we locked scroll when opening, we must unlock it now,
         * regardless of whether the user rotated to desktop.
         */
        if (this.openedOnMobile) {
          CustomDropdown.decrementScrollLock();
        }

        // Reset viewport state for next open
        this.openedOnMobile = false;
      }

      /**
       * Subscribes to variant change events from PubSub.
       * Updates inventory display when other options change.
       */
      subscribeToVariantChanges() {
        // Check if Shopify's PubSub system is available
        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          // Store subscription reference for cleanup in disconnectedCallback
          this.variantSubscription = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            () => {
              this.updateInventoryDisplay();
            },
          );
        }
      }
    },
  );
}
